import { useEffect } from "react"
import { useForm, Controller } from "react-hook-form"
import { Input, Select } from "../../../../../components"
import styles from "./identity-information.module.scss"
import { useSaveZambiaEmployeeIdentityInfoMutation } from "../../../../../../../service/approval-service"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { RootState } from "../../../../../../../stores/store"

interface IdentityFormValues {
  idType: string
  idNumber: string
}

const idTypeOptions = [
  { label: "Id", value: "Id" },
  { label: "Passport", value: "Passport" },
]

interface IdentityInformationProps {
  onCancel: () => void;
  onContinue: () => void;
}

export const IdentityInformation = ({ onCancel, onContinue }: IdentityInformationProps) => {
  const creditInfo = useSelector((state: RootState) => state.getSolar.creditInfo);

  const [saveZambiaEmployeeIdentityInfo, { isLoading: isSaving }] = useSaveZambiaEmployeeIdentityInfoMutation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IdentityFormValues>({
    defaultValues: {
      idType: "",
      idNumber: "",
    },
  })

  useEffect(() => {
    const fetchCreditInfo = async () => {
      try {
        if (creditInfo?.employeeData?.identityData != null) {
          reset({
            idType: creditInfo.employeeData.identityData.idType ?? "",
            idNumber: creditInfo.employeeData.identityData.idNumber ?? "",
          });
        }
      } catch (error) {
        toast.error("Failed to fetch credit information")
      }
    };
    fetchCreditInfo();
  }, [creditInfo, reset]);

  const onSubmit = async (data: IdentityFormValues) => {
    const formData = new FormData();
    formData.append("idType", data.idType);
    formData.append("idNumber", data.idNumber);

    try {
      await saveZambiaEmployeeIdentityInfo(formData).unwrap();
      onContinue();
    } catch (error) {
      toast.error("Failed to submit identity information");
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>Identity Information</h2>
          <p className={styles.subtitle}>Verify Identity information</p>
        </div>

        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="idType"
            control={control}
            rules={{ required: "Please select a preferred ID" }}
            render={({ field }) => (
              <Select
                options={idTypeOptions}
                value={field.value}
                onChange={field.onChange}
                placeholder="Select Preferred ID"
                error={errors.idType?.message}
              />
            )}
          />

          <Controller
            name="idNumber"
            control={control}
            rules={{
              required: "ID number is required",
              pattern: {
                value: /^\d+$/,
                message: "ID number can only contain numbers"
              }
            }}
            render={({ field }) => (
              <Input
                type="text"
                placeholder="Enter ID Number"
                value={field.value}
                onChange={field.onChange}
                variant="solid"
                error={errors.idNumber?.message}
              />
            )}
          />

          <div className={styles.footer}>
            <div className={styles.footerInner}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.continueBtn}
                disabled={isSaving}
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
